/**
 * Beurteilung eines Befundes.
 *
 * Erscheint als Blatt von unten, damit der Daumen alles erreicht. Aufbau von
 * oben nach unten in der Reihenfolge, in der gearbeitet wird: Stufe wählen,
 * bei einem Mangel den Text setzen, Foto machen, speichern.
 */

import { Fragment, useRef, useState } from 'react'
import { useBausteine, useFotos, useGeraetetypen } from '../api/abfragen'
import {
  MANGELSTATUSTEXT,
  STUFEN,
  STUFENTEXT,
  istMangel,
  stufenklasse,
  type BefundEingabe,
  type Geraetebefund,
  type Platzbefund,
  type Stufe,
} from '../api/typen'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Stufenzeichen, datumDeutsch } from '../bausteine/Hinweise'

type Eigenschaften = {
  befund: Platzbefund | Geraetebefund
  istGeraet: boolean
  /** Für die Fotos in der Leseansicht: der Befund selbst trägt sie nicht. */
  pruefungId: string
  /** Gerätetyp, um passende Bausteine vorzuschlagen. */
  geraetetyp?: string | null
  beimSpeichern: (eingabe: BefundEingabe) => Promise<void>
  beimZuruecksetzen: () => Promise<void>
  beimFoto: (datei: File, art: 'uebersicht' | 'detail') => Promise<void>
  bausteinVerwendet: (id: string) => Promise<void>
  schliessen: () => void
  /** Nach dem Speichern gleich zum nächsten offenen Punkt springen. */
  weiterZumNaechsten: () => void
  nurLesen: boolean
  /** Nur bei Geräten belegt: öffnet den Schrittablauf. */
  tunnelOeffnen?: () => void
  /** Zuletzt in dieser Prüfung eingetragener Normverweis. */
  letzteNorm?: string | null
  /** Merkt sich den eben verwendeten Normverweis für den nächsten Punkt. */
  normGemerkt?: (norm: string | null) => void
}

export function BefundBearbeiten({
  befund,
  istGeraet,
  pruefungId,
  geraetetyp,
  beimSpeichern,
  beimZuruecksetzen,
  beimFoto,
  bausteinVerwendet,
  schliessen,
  weiterZumNaechsten,
  nurLesen,
  tunnelOeffnen,
  letzteNorm,
  normGemerkt,
}: Eigenschaften) {
  const typen = useGeraetetypen()
  const [stufe, setzeStufe] = useState<Stufe | null>(befund.stufe)
  const [mangel, setzeMangel] = useState(befund.mangel ?? '')
  const [bemerkung, setzeBemerkung] = useState(befund.bemerkung ?? '')
  /* Der Normverweis, vorbelegt statt leer.
     
     Drei Quellen, von genau nach ungefähr:
     
       1. was an diesem Punkt schon steht — daran ändert nichts etwas,
       2. der Normverweis der Vorprüfung an genau diesem Punkt — an
          derselben Schaukel gilt dieses Jahr dieselbe Norm wie im letzten,
       3. die Norm des Gerätetyps aus dem Katalog — eine Rutsche folgt
          DIN EN 1176-3, eine Schaukel DIN EN 1176-2,
       4. der zuletzt in dieser Prüfung eingetragene Verweis.
     
     Die Reihenfolge ist wichtig: (4) allein wäre falsch, sobald der
     nächste Punkt ein anderer Gerätetyp ist. Deshalb steht die
     typgebundene Norm davor. Überschreiben lässt sich alles. */
  const typnorm = (typen.data ?? []).find((x) => x.name === geraetetyp)?.normteil ?? null
  const [normverweis, setzeNormverweis] = useState(
    befund.normverweis ??
      befund.vorjahr_normverweis ??
      typnorm ??
      letzteNorm ??
      '',
  )
  const [frist, setzeFrist] = useState(
    istGeraet ? ((befund as Geraetebefund).frist_am ?? '') : '',
  )
  /* Erwartet der Prüfer, dass der Kunde die Behebung meldet?

     Vorgabe ja, das ist der Normalfall. Abwählen heißt: dieser Kunde nutzt
     das Portal nicht, die Behebung wird bei der Nachprüfung selbst
     festgestellt. Ohne diesen Schalter stünde der Mangel dauerhaft als
     „offen, wartet auf den Kunden" da, und niemand könnte unterscheiden,
     ob nichts geschehen oder nur nichts gemeldet worden ist. */
  const [meldungErwartet, setzeMeldungErwartet] = useState(
    istGeraet ? ((befund as Geraetebefund).meldung_erwartet ?? true) : true,
  )
  const [laeuft, setzeLaeuft] = useState(false)
  const [fehler, setzeFehler] = useState<unknown>(null)
  const [fotoLaeuft, setzeFotoLaeuft] = useState(false)

  const mangelBausteine = useBausteine('mangel')
  const bemerkungBausteine = useBausteine('bemerkung')
  const normBausteine = useBausteine('normverweis')

  /* Passende Bausteine zuerst, dann die allgemeinen.
     Nur die passenden zu zeigen wäre zu streng: ein allgemeiner Text wie
     „Verschleiß im üblichen Rahmen“ gehört überall hin. Nur die
     allgemeinen zu zeigen wäre zu grob. Also beides, in dieser
     Reihenfolge — was zum Gerät gehört, steht oben und wird zuerst
     gefunden. */
  function passendZuerst<T extends { geraetetyp?: string | null }>(liste: T[]): T[] {
    const eigene = liste.filter((b) => b.geraetetyp && b.geraetetyp === geraetetyp)
    const allgemein = liste.filter((b) => !b.geraetetyp)
    return [...eigene, ...allgemein]
  }

  const kamera = useRef<HTMLInputElement>(null)

  const brauchtMangeltext = stufe !== null && istMangel(stufe)
  const kannSpeichern =
    stufe !== null && (!brauchtMangeltext || mangel.trim().length > 0) && !laeuft

  async function speichern() {
    if (stufe === null) return
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      await beimSpeichern({
        stufe,
        mangel: mangel.trim() || null,
        bemerkung: bemerkung.trim() || null,
        normverweis: normverweis.trim() || null,
        frist_am: frist || null,
        meldung_erwartet: meldungErwartet,
      })
      // Für den nächsten Punkt merken – dort die letzte der vier Quellen.
      normGemerkt?.(normverweis.trim() || null)
      weiterZumNaechsten()
    } catch (f) {
      setzeFehler(f)
    } finally {
      setzeLaeuft(false)
    }
  }

  /** Baustein einsetzen und seine Verwendung zählen. */
  async function bausteinNehmen(
    id: string,
    text: string,
    stufenvorschlag: Stufe | null,
    norm: string | null,
    ziel: 'mangel' | 'bemerkung' | 'norm',
  ) {
    if (ziel === 'mangel') {
      setzeMangel((alt) => (alt.trim() ? `${alt.trim()}, ${text}` : text))
      // Der Stufenvorschlag greift nur, wenn noch keine Stufe gewählt ist –
      // die Entscheidung des Prüfers wird nicht überschrieben.
      if (stufenvorschlag && stufe === null) setzeStufe(stufenvorschlag)
      if (norm && !normverweis) setzeNormverweis(norm)
    } else if (ziel === 'bemerkung') {
      setzeBemerkung((alt) => (alt.trim() ? `${alt.trim()}, ${text}` : text))
    } else {
      setzeNormverweis(text)
    }
    await bausteinVerwendet(id)
  }

  const titel = befund.bezeichnung
  const untertitel = istGeraet
    ? [geraetetyp, (befund as Geraetebefund).hersteller, (befund as Geraetebefund).baujahr]
        .filter(Boolean)
        .join(' · ')
    : (befund as Platzbefund).gruppe

  // Die Knöpfe liegen in der festen Fußzeile des Blattes – auf dem Handy im
  // Daumenbereich, auf dem Rechner am unteren Rand des Fensters.
  const fuss = nurLesen ? undefined : (
    <div className="flex gap-2">
      <button
        type="button"
        className="knopf-haupt flex-1"
        onClick={() => void speichern()}
        disabled={!kannSpeichern}
      >
        {laeuft ? 'Speichert …' : 'Übernehmen und weiter'}
      </button>
      {befund.beurteilt_am && (
        <button
          type="button"
          className="knopf-warnung px-3"
          onClick={() => {
            if (
              window.confirm(
                'Beurteilung zurücknehmen? Der Punkt gilt danach wieder als ' +
                  'nicht beurteilt.',
              )
            ) {
              void beimZuruecksetzen().then(schliessen)
            }
          }}
        >
          Zurücknehmen
        </button>
      )}
    </div>
  )

  return (
    <Blatt titel={titel} untertitel={untertitel} schliessen={schliessen} fuss={fuss}>
      <>
        {/* Vorjahresstand: der wichtigste Hinweis draußen am Gerät. */}
        {befund.vorjahr_stufe && (
          <div className="mb-3 rounded-feld border-2 border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-800">Vorjahr:</span>
              <Stufenzeichen stufe={befund.vorjahr_stufe} klein />
              <span className="text-base text-slate-700">
                {STUFENTEXT[befund.vorjahr_stufe]}
              </span>
            </div>
            {befund.vorjahr_mangel && (
              <p className="mt-1 text-base text-slate-700">{befund.vorjahr_mangel}</p>
            )}
          </div>
        )}

        {(befund as Platzbefund).hinweis && (
          <p className="mb-3 text-base text-slate-700">{(befund as Platzbefund).hinweis}</p>
        )}

        {/* Der Ablauf steht oben: wer unsicher ist, soll ihn nehmen und nicht
            erst die Stufenwahl sehen. */}
        {tunnelOeffnen && !nurLesen && (
          <button
            type="button"
            className="knopf-zweit mb-4 w-full"
            onClick={tunnelOeffnen}
            style={{ minHeight: '56px' }}
          >
            Schritt für Schritt prüfen
          </button>
        )}

        {nurLesen ? (
          <Gelesen befund={befund} istGeraet={istGeraet} pruefungId={pruefungId} />
        ) : (
          <>
            <fieldset className="mb-4">
              <legend className="mb-2 font-semibold text-slate-800">Mängelstufe</legend>
              <div className="grid grid-cols-5 gap-2">
                {STUFEN.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setzeStufe(s)}
                    aria-pressed={stufe === s}
                    title={STUFENTEXT[s]}
                    className={`${stufenklasse(s)} flex-col gap-0 px-1 text-lg ${
                      stufe === s ? 'ring-4 ring-marke-600 ring-offset-1' : 'opacity-70'
                    }`}
                    style={{ minHeight: '64px' }}
                  >
                    <span>{s}</span>
                  </button>
                ))}
              </div>
              {stufe && (
                <p className="mt-2 text-base font-semibold text-slate-800">
                  {STUFENTEXT[stufe]}
                  {stufe === '3' && ' – das Gerät wird gesperrt'}
                </p>
              )}
            </fieldset>

            {brauchtMangeltext && (
              <div className="mb-4">
                <label htmlFor="mangel">
                  Mangel <span className="text-stufe-3">*</span>
                </label>
                <textarea
                  id="mangel"
                  value={mangel}
                  onChange={(e) => setzeMangel(e.target.value)}
                  placeholder="Was ist zu beanstanden?"
                />
                {mangel.trim().length === 0 && (
                  <p className="mt-1 text-base text-stufe-2">
                    Bei Stufe {stufe} muss der Mangel beschrieben werden – der Bauhof soll
                    aus dem Bericht ablesen können, was zu tun ist.
                  </p>
                )}
                <Bausteinliste
                  titel="Häufige Mängel"
                  bausteine={passendZuerst(mangelBausteine.data ?? [])}
                  beimWaehlen={(b) =>
                    void bausteinNehmen(b.id, b.text, b.stufe_vorschlag, b.normverweis, 'mangel')
                  }
                />
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="bemerkung">Bemerkung / Hinweis an den Kunden</label>
              <textarea
                id="bemerkung"
                value={bemerkung}
                onChange={(e) => setzeBemerkung(e.target.value)}
                style={{ minHeight: '72px' }}
              />
              <Bausteinliste
                titel="Häufige Bemerkungen"
                bausteine={passendZuerst(bemerkungBausteine.data ?? [])}
                beimWaehlen={(b) => void bausteinNehmen(b.id, b.text, null, null, 'bemerkung')}
              />
            </div>

            <div className="mb-4 grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="normverweis">Normverweis</label>
                <input
                  id="normverweis"
                  value={normverweis}
                  onChange={(e) => setzeNormverweis(e.target.value)}
                  placeholder="DIN EN 1176-2"
                />
                <Bausteinliste
                  titel="Normen"
                  bausteine={passendZuerst(normBausteine.data ?? [])}
                  beimWaehlen={(b) => void bausteinNehmen(b.id, b.text, null, null, 'norm')}
                />
              </div>
              {istGeraet && brauchtMangeltext && (
                <div>
                  <label htmlFor="frist">Frist zur Behebung</label>
                  <input
                    id="frist"
                    type="date"
                    value={frist}
                    onChange={(e) => setzeFrist(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Nur bei einem Mangel: ohne Mangel gibt es nichts zu melden. */}
            {istGeraet && brauchtMangeltext && (
              <div className="mb-4 rounded-feld border-2 border-slate-200 p-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 size-6 shrink-0"
                    checked={meldungErwartet}
                    onChange={(e) => setzeMeldungErwartet(e.target.checked)}
                    disabled={nurLesen}
                  />
                  <span>
                    <span className="font-semibold">
                      Kunde meldet die Behebung
                    </span>
                    <span className="mt-1 block text-base font-normal text-slate-600">
                      {meldungErwartet
                        ? 'Der Kunde bekommt im Portal einen Knopf, um die Behebung zu melden. Bestätigen können nur Sie, bei der Nachprüfung.'
                        : 'Der Kunde meldet nichts. Im Portal und im Bericht steht, dass die Behebung bei der nächsten Prüfung vor Ort festgestellt wird.'}
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Fotos */}
            <div className="mb-4">
              <label>Fotos ({befund.anzahl_fotos})</label>
              <input
                ref={kamera}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={async (e) => {
                  const datei = e.target.files?.[0]
                  e.target.value = ''
                  if (!datei) return
                  setzeFotoLaeuft(true)
                  setzeFehler(null)
                  try {
                    await beimFoto(datei, befund.anzahl_fotos === 0 ? 'uebersicht' : 'detail')
                  } catch (f) {
                    setzeFehler(f)
                  } finally {
                    setzeFotoLaeuft(false)
                  }
                }}
              />
              <button
                type="button"
                className="knopf-zweit w-full"
                onClick={() => kamera.current?.click()}
                disabled={fotoLaeuft}
              >
                {fotoLaeuft
                  ? 'Verarbeitet …'
                  : befund.anzahl_fotos === 0
                    ? 'Übersichtsfoto aufnehmen'
                    : 'Weiteres Foto aufnehmen'}
              </button>
            </div>

            <Fehlerhinweis fehler={fehler} />
          </>
        )}
      </>
    </Blatt>
  )
}

/** Textbausteine als antippbare Liste, nach Häufigkeit sortiert. */
function Bausteinliste({
  titel,
  bausteine,
  beimWaehlen,
}: {
  titel: string
  bausteine: { id: string; text: string; stufe_vorschlag: Stufe | null }[]
  beimWaehlen: (b: {
    id: string
    text: string
    stufe_vorschlag: Stufe | null
    normverweis: string | null
  }) => void
}) {
  const [alleZeigen, setzeAlleZeigen] = useState(false)
  const [suche, setzeSuche] = useState('')
  if (bausteine.length === 0) return null

  /* Suchen statt scrollen. Die eingeklappte Liste zeigt sechs Bausteine;
     bei fünfzig im Katalog findet man einen bestimmten sonst nur durch
     Aufklappen und Lesen. Sobald etwas getippt ist, wird der ganze
     Katalog durchsucht – auch das, was eingeklappt verborgen wäre. */
  const gefiltert = suche.trim()
    ? bausteine.filter((b) => b.text.toLowerCase().includes(suche.trim().toLowerCase()))
    : bausteine

  const sichtbar = alleZeigen || suche.trim() ? gefiltert : gefiltert.slice(0, 6)
  const verborgen = gefiltert.length - sichtbar.length

  return (
    <div className="mt-2">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <p className="text-base font-semibold text-slate-700">{titel}</p>
        {bausteine.length > 6 && (
          <input
            value={suche}
            onChange={(e) => setzeSuche(e.target.value)}
            placeholder={`${bausteine.length} durchsuchen …`}
            className="ml-auto max-w-[14rem] flex-1"
            style={{ minHeight: '40px' }}
            aria-label={`${titel} durchsuchen`}
          />
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {sichtbar.map((b) => (
          <button
            key={b.id}
            type="button"
            onClick={() => beimWaehlen(b as never)}
            className="rounded-full border-2 border-slate-300 bg-white px-3 py-1.5 text-left
                       text-base hover:border-marke-500 hover:bg-marke-50"
            style={{ minHeight: '44px' }}
          >
            {b.stufe_vorschlag && (
              <span className="mr-1 font-bold text-slate-600">[{b.stufe_vorschlag}]</span>
            )}
            {b.text}
          </button>
        ))}
        {/* Beim Suchen kein Aufklappknopf: dann ist ohnehin alles zu sehen,
            was passt. Stattdessen die Zahl der Treffer. */}
        {suche.trim() ? (
          <span className="px-2 py-1.5 text-base text-slate-600">
            {gefiltert.length === 0
              ? 'kein Baustein passt'
              : `${gefiltert.length} von ${bausteine.length}`}
          </span>
        ) : (
          verborgen > 0 && (
            <button
              type="button"
              onClick={() => setzeAlleZeigen((a) => !a)}
              className="rounded-full px-3 py-1.5 text-base font-semibold text-akzent-700 underline"
              style={{ minHeight: '44px' }}
            >
              {`${verborgen} weitere zeigen`}
            </button>
          )
        )}
        {alleZeigen && !suche.trim() && (
          <button
            type="button"
            onClick={() => setzeAlleZeigen(false)}
            className="rounded-full px-3 py-1.5 text-base font-semibold text-akzent-700 underline"
            style={{ minHeight: '44px' }}
          >
            weniger
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Ein abgeschlossener Befund, zum Nachlesen.
 *
 * Hier stand vorher nur Stufe, Mangel und Bemerkung. Alles andere war
 * erfasst und im Bericht zu sehen, aber nicht in der Prüfung selbst – wer
 * ein halbes Jahr später wissen wollte, welche Norm herangezogen wurde,
 * welche Frist gesetzt war oder wie es im Vorjahr aussah, musste das PDF
 * heraussuchen. Die Daten sind da; sie waren nur nicht angezeigt.
 */
function Gelesen({
  befund,
  istGeraet,
  pruefungId,
}: {
  befund: Platzbefund | Geraetebefund
  istGeraet: boolean
  pruefungId: string
}) {
  const geraet = istGeraet ? (befund as Geraetebefund) : null
  const fotos = useFotos(pruefungId)

  const meine = (fotos.data ?? []).filter(
    (f) => f.geraetebefund_id === befund.id || f.platzbefund_id === befund.id,
  )

  /* Nur zeigen, was dasteht.
   *
   * Auf den Rohwert geprüft und nicht auf den formatierten: `datumDeutsch`
   * gibt für „nichts" einen Gedankenstrich zurück, und der ist wahr. Sonst
   * stünde „Frist zur Behebung —" auch dort, wo keine Frist gesetzt war,
   * und das liest sich wie eine Angabe. */
  const zeilen: [string, string][] = [
    ...(befund.normverweis ? [['Normverweis', befund.normverweis]] : []),
    ...(befund.beurteilt_am
      ? [['Beurteilt am', datumDeutsch(befund.beurteilt_am)]]
      : []),
    ...(geraet?.frist_am
      ? [['Frist zur Behebung', datumDeutsch(geraet.frist_am)]]
      : []),
    ...(geraet?.mangelstatus
      ? [['Stand des Mangels', MANGELSTATUSTEXT[geraet.mangelstatus]]]
      : []),
    ...(geraet?.gesperrt ? [['Gerät gesperrt', 'ja']] : []),
  ] as [string, string][]

  return (
    <div className="space-y-3">
      <div className="karte space-y-2">
        <div className="flex items-center gap-2">
          <Stufenzeichen stufe={befund.stufe} />
          <span className="font-semibold">
            {befund.stufe ? STUFENTEXT[befund.stufe] : 'nicht beurteilt'}
          </span>
        </div>
        {befund.mangel && <p>{befund.mangel}</p>}
        {befund.bemerkung && <p className="text-slate-700">{befund.bemerkung}</p>}

        {zeilen.length > 0 && (
          <dl className="grid gap-x-4 gap-y-1 pt-1 text-base sm:grid-cols-[11rem_1fr]">
            {zeilen.map(([text, wert]) => (
              <Fragment key={text}>
                <dt className="text-slate-600">{text}</dt>
                <dd className="font-semibold text-slate-800">{wert}</dd>
              </Fragment>
            ))}
          </dl>
        )}

        <p className="text-base text-slate-600">
          Die Prüfung ist abgeschlossen und nicht mehr änderbar.
        </p>
      </div>

      {/* Der Vergleich zum Vorjahr. Er steht im Bericht und gehört auch
          hierher: ob ein Mangel neu ist oder seit einem Jahr besteht, ist
          die Frage, an der die Verkehrssicherungspflicht hängt. */}
      {(befund.vorjahr_stufe || befund.vorjahr_mangel) && (
        <div className="karte space-y-1">
          <h3 className="font-semibold">Vorprüfung</h3>
          <div className="flex items-center gap-2">
            <Stufenzeichen stufe={befund.vorjahr_stufe ?? null} />
            <span>
              {befund.vorjahr_stufe
                ? STUFENTEXT[befund.vorjahr_stufe]
                : 'nicht beurteilt'}
            </span>
          </div>
          {befund.vorjahr_mangel && (
            <p className="text-slate-700">{befund.vorjahr_mangel}</p>
          )}
          {befund.vorjahr_normverweis && (
            <p className="text-base text-slate-600">
              Norm: {befund.vorjahr_normverweis}
            </p>
          )}
        </div>
      )}

      <div className="karte space-y-2">
        <h3 className="font-semibold">Fotos ({meine.length})</h3>
        {fotos.isLoading ? (
          <p className="text-base text-slate-600">Lädt …</p>
        ) : meine.length === 0 ? (
          <p className="text-base text-slate-600">
            Zu diesem Punkt wurde kein Foto aufgenommen.
          </p>
        ) : (
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {meine.map((f) => (
              <li key={f.id}>
                <a
                  href={`/api/fotos/${f.id}`}
                  target="_blank"
                  rel="noreferrer"
                  title={f.beschriftung ?? 'Foto öffnen'}
                >
                  <img
                    src={`/api/fotos/${f.id}`}
                    alt={f.beschriftung ?? 'Foto zum Befund'}
                    loading="lazy"
                    className="aspect-square w-full rounded-feld border-2 border-slate-200 object-cover"
                  />
                </a>
                {f.beschriftung && (
                  <p className="mt-1 text-sm text-slate-600">{f.beschriftung}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
